/**
 * ShipInteriorSystem.js
 *
 * June 2026 — Walkable Ship Interiors (Hangar feature).
 *
 * The four larger ships (Hauler, Voyager, Gunship, Monarch) expose a walkable
 * cabin. While the ship is landed on a surface the pilot can board the interior
 * on foot: an elongated hull built on demand at a far world offset (so it never
 * overlaps the surface or the station/cave interiors).
 *
 * Layout (local, +Z forward):
 *   - Bridge at the front (+Z): pilot seats + a starfield/planet viewport.
 *   - Central corridor / cargo hold: crates + ceiling lights.
 *   - Engine room at the rear (-Z): a glowing reactor core.
 *   - An exit hatch (green pad) to return to the cockpit.
 *
 * Mirrors StationInteriorSystem's lifecycle so GameScene reuses the same walking
 * code (it swaps the ground-height source to this flat floor while inside).
 *
 * Public API:
 *   buildInterior(seed, shipDef)
 *   clearInterior()
 *   showInterior(visible)
 *   floorHeight(x,z)
 *   clamp(x,z)
 *   getEntryPoint()
 *   getExitPos()
 *   getNearExit(radius) -> { distance } | null
 *   tick(dt)
 *   dispose()
 */

import {
  Vector3,
  Color3,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
  PointLight,
} from '@babylonjs/core';

// Far from surface(0,0), caves(5000,5000) and station(8000,8000).
const SX = 12000;
const SZ = 12000;
const FLOOR_Y = 0;
const EXIT_RADIUS = 3.4;

function mkRng(seed) {
  let s = (seed | 0) >>> 0;
  if (!s) s = 0xbeef;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

export default class ShipInteriorSystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.getPlayerPos = opts.getPlayerPos || (() => new Vector3(0, 0, 0));
    this.glow = opts.glow || null;
    this.SX = SX;
    this.SZ = SZ;
    this._root = null;
    this._t = 0;
    this._core = null;
    this._halfW = 5;
    this._halfD = 12;
    this._wallH = 4.5;
    this._entryPos = new Vector3(SX, FLOOR_Y, SZ);
    this._exitPos = new Vector3(SX, FLOOR_Y, SZ - 8);
  }

  floorHeight() { return FLOOR_Y; }

  clamp(x, z) {
    const lx = x - SX;
    const lz = z - SZ;
    const mx = this._halfW - 1.0;
    const mz = this._halfD - 1.0;
    return {
      x: SX + Math.max(-mx, Math.min(mx, lx)),
      z: SZ + Math.max(-mz, Math.min(mz, lz)),
    };
  }

  getEntryPoint() { return this._entryPos.clone(); }
  getExitPos() { return this._exitPos.clone(); }

  getNearExit(radius = EXIT_RADIUS) {
    const p = this.getPlayerPos();
    const d = Math.hypot(p.x - this._exitPos.x, p.z - this._exitPos.z);
    return d < radius ? { distance: d } : null;
  }

  getNearFabricator(radius = 3.2) {
    if (!this._fabPos) return null;
    const p = this.getPlayerPos();
    const d = Math.hypot(p.x - this._fabPos.x, p.z - this._fabPos.z);
    return d < radius ? { distance: d } : null;
  }

  _mat(name, diffuse, opts = {}) {
    const m = new StandardMaterial(name, this.scene);
    m.diffuseColor = diffuse;
    m.specularColor = opts.specular || new Color3(0.15, 0.16, 0.2);
    if (opts.emissive) m.emissiveColor = opts.emissive;
    m.maxSimultaneousLights = 8;
    return m;
  }

  _emissive(name, color, alpha) {
    const m = new StandardMaterial(name, this.scene);
    m.diffuseColor = new Color3(0, 0, 0);
    m.emissiveColor = color;
    m.disableLighting = true;
    if (alpha != null) m.alpha = alpha;
    return m;
  }

  buildInterior(seed, shipDef) {
    this.clearInterior();
    const rng = mkRng((seed | 0) >>> 0 || 13);
    const xlarge = (shipDef?.size === 'xlarge');
    const HALF_W = xlarge ? 6 : 5;
    const HALF_D = xlarge ? 15 : 12;
    const WALL_H = xlarge ? 5.0 : 4.5;
    this._halfW = HALF_W; this._halfD = HALF_D; this._wallH = WALL_H;
    // Spawn mid-hull near the bridge; exit hatch sits behind the spawn.
    this._entryPos = new Vector3(SX, FLOOR_Y, SZ + HALF_D * 0.2);
    this._exitPos = new Vector3(SX, FLOOR_Y, SZ + HALF_D * 0.2 - 4.5);

    const root = new TransformNode('shipInteriorRoot', this.scene);
    this._root = root;

    // Theme tint per ship.
    const themes = {
      hauler: new Color3(0.4, 0.38, 0.3),
      voyager: new Color3(0.32, 0.4, 0.46),
      gunship: new Color3(0.26, 0.27, 0.3),
      monarch: new Color3(0.5, 0.48, 0.42),
    };
    const baseTint = themes[shipDef?.id] || new Color3(0.34, 0.36, 0.42);
    const deckMat = this._mat('siDeck', baseTint.scale(0.8), { specular: new Color3(0.3, 0.32, 0.38) });
    const wallMat = this._mat('siWall', baseTint, { specular: new Color3(0.2, 0.22, 0.28) });
    const trimMat = this._mat('siTrim', new Color3(0.16, 0.18, 0.24), { emissive: new Color3(0.05, 0.18, 0.26) });

    // Floor (rounded ends suggested by an inner ellipse stripe).
    const floor = MeshBuilder.CreateBox('siFloor', { width: HALF_W * 2, height: 0.5, depth: HALF_D * 2 }, this.scene);
    floor.position.set(SX, FLOOR_Y - 0.25, SZ); floor.material = deckMat; floor.parent = root;
    floor.isPickable = false; floor.receiveShadows = true;
    // Ceiling
    const ceil = MeshBuilder.CreateBox('siCeil', { width: HALF_W * 2, height: 0.4, depth: HALF_D * 2 }, this.scene);
    ceil.position.set(SX, FLOOR_Y + WALL_H, SZ); ceil.material = wallMat; ceil.parent = root; ceil.isPickable = false;

    const mkWall = (n, w, d, x, z) => {
      const wall = MeshBuilder.CreateBox(n, { width: w, height: WALL_H, depth: d }, this.scene);
      wall.position.set(SX + x, FLOOR_Y + WALL_H / 2, SZ + z);
      wall.material = wallMat; wall.parent = root; wall.isPickable = false;
      return wall;
    };
    mkWall('siWallE', 0.5, HALF_D * 2, HALF_W, 0);
    mkWall('siWallW', 0.5, HALF_D * 2, -HALF_W, 0);
    mkWall('siWallBack', HALF_W * 2, 0.5, 0, HALF_D);   // rear wall
    // Front (bridge) wall holds the viewport — built below.
    mkWall('siWallFrontL', HALF_W - 2, 0.5, -(HALF_W / 2 + 0.5), -HALF_D);
    mkWall('siWallFrontR', HALF_W - 2, 0.5, (HALF_W / 2 + 0.5), -HALF_D);

    // Centre corridor stripe.
    const stripe = MeshBuilder.CreateBox('siStripe', { width: 1.6, height: 0.06, depth: HALF_D * 2 - 1 }, this.scene);
    stripe.position.set(SX, FLOOR_Y + 0.05, SZ); stripe.material = trimMat; stripe.parent = root; stripe.isPickable = false;

    // ---- Bridge viewport (front, -Z) ----
    const view = MeshBuilder.CreatePlane('siView', { width: HALF_W * 1.4, height: 2.4 }, this.scene);
    view.position.set(SX, FLOOR_Y + 2.4, SZ - HALF_D + 0.3); view.parent = root;
    view.material = this._emissive('siViewMat', new Color3(0.03, 0.05, 0.12));
    view.isPickable = false;
    for (let i = 0; i < 30; i++) {
      const star = MeshBuilder.CreatePlane(`siStar_${i}`, { size: 0.06 + rng() * 0.1 }, this.scene);
      star.position.set(SX - HALF_W * 0.65 + rng() * HALF_W * 1.3, FLOOR_Y + 1.4 + rng() * 2.0, SZ - HALF_D + 0.28);
      star.parent = root;
      star.material = this._emissive(`siStarMat_${i}`, new Color3(0.8, 0.85, 1.0));
      star.isPickable = false;
    }
    // Pilot seats at the bridge.
    [-1.2, 1.2].forEach((x, i) => {
      const seat = MeshBuilder.CreateBox(`siSeat_${i}`, { width: 0.9, height: 1.1, depth: 0.9 }, this.scene);
      seat.position.set(SX + x, FLOOR_Y + 0.55, SZ - HALF_D + 2.0); seat.parent = root;
      seat.material = this._mat(`siSeatMat_${i}`, new Color3(0.15, 0.16, 0.2));
      seat.isPickable = false;
      const console_ = MeshBuilder.CreateBox(`siConsole_${i}`, { width: 1.0, height: 0.5, depth: 0.5 }, this.scene);
      console_.position.set(SX + x, FLOOR_Y + 0.9, SZ - HALF_D + 1.0); console_.rotation.x = -0.3; console_.parent = root;
      console_.material = this._emissive(`siConsoleMat_${i}`, new Color3(0.12, 0.5, 0.6));
      console_.isPickable = false;
      if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(console_); } catch (_e) { /* noop */ } }
    });

    // ---- Cargo hold (middle): crates ----
    const crateN = xlarge ? 8 : 6;
    for (let i = 0; i < crateN; i++) {
      const cw = 0.8 + rng() * 0.9;
      const crate = MeshBuilder.CreateBox(`siCrate_${i}`, { width: cw, height: cw, depth: cw }, this.scene);
      const side = i % 2 === 0 ? -1 : 1;
      crate.position.set(SX + side * (HALF_W - 1.4 - rng() * 1.5), FLOOR_Y + cw / 2, SZ - 2 + rng() * (HALF_D - 1));
      crate.rotation.y = rng() * Math.PI; crate.parent = root;
      crate.material = this._mat(`siCrateMat_${i}`, new Color3(0.3, 0.27 + rng() * 0.1, 0.2), { emissive: new Color3(0.04, 0.03, 0.02) });
      crate.isPickable = false;
    }

    // ---- Fabricator terminal (port wall, mid-hull) — opens the Crafting UI ----
    this._fabPos = new Vector3(SX - HALF_W + 1.0, FLOOR_Y, SZ + 1.0);
    const fab = MeshBuilder.CreateBox('siFab', { width: 1.6, height: 2.0, depth: 0.7 }, this.scene);
    fab.position.set(this._fabPos.x, FLOOR_Y + 1.0, this._fabPos.z); fab.parent = root;
    fab.material = this._mat('siFabMat', new Color3(0.28, 0.2, 0.1), { emissive: new Color3(0.5, 0.3, 0.05) });
    fab.isPickable = false;
    const fabScreen = MeshBuilder.CreatePlane('siFabScreen', { width: 1.2, height: 0.9 }, this.scene);
    fabScreen.position.set(this._fabPos.x + 0.4, FLOOR_Y + 1.4, this._fabPos.z);
    fabScreen.rotation.y = Math.PI / 2; fabScreen.parent = root;
    fabScreen.material = this._emissive('siFabScreenMat', new Color3(0.95, 0.6, 0.15), 0.92);
    fabScreen.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(fabScreen); } catch (_e) { /* noop */ } }
    const fabLight = new PointLight('siFabLight', new Vector3(this._fabPos.x + 0.8, FLOOR_Y + 1.6, this._fabPos.z), this.scene);
    fabLight.diffuse = new Color3(1.0, 0.7, 0.3); fabLight.intensity = 0.5; fabLight.range = 8; fabLight.parent = root;

    // ---- Engine room (rear, +Z): glowing reactor core ----
    const core = MeshBuilder.CreateCylinder('siCore', { diameter: 1.6, height: 2.6, tessellation: 20 }, this.scene);
    core.position.set(SX, FLOOR_Y + 1.5, SZ + HALF_D - 2.0); core.parent = root;
    core.material = this._emissive('siCoreMat', new Color3(0.2, 0.7, 0.95), 0.85);
    core.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(core); } catch (_e) { /* noop */ } }
    this._core = core;
    const coreLight = new PointLight('siCoreLight', new Vector3(SX, FLOOR_Y + 1.8, SZ + HALF_D - 2.0), this.scene);
    coreLight.diffuse = new Color3(0.4, 0.8, 1.0); coreLight.intensity = 0.7; coreLight.range = 16; coreLight.parent = root;
    // Reactor housing pillars.
    [-1, 1].forEach((s) => {
      const pillar = MeshBuilder.CreateBox(`siPillar${s}`, { width: 0.5, height: WALL_H, depth: 0.5 }, this.scene);
      pillar.position.set(SX + s * 1.6, FLOOR_Y + WALL_H / 2, SZ + HALF_D - 2.0); pillar.material = wallMat; pillar.parent = root;
      pillar.isPickable = false;
    });

    // ---- Exit hatch (green pad) ----
    const frame = MeshBuilder.CreateTorus('siHatch', { diameter: 3.4, thickness: 0.4, tessellation: 24 }, this.scene);
    frame.rotation.x = Math.PI / 2; frame.position.set(this._exitPos.x, FLOOR_Y + 1.8, this._exitPos.z); frame.parent = root;
    frame.material = this._mat('siHatchMat', new Color3(0.1, 0.18, 0.14), { emissive: new Color3(0.2, 1.0, 0.5) });
    frame.material.specularColor = new Color3(0, 0, 0);
    frame.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(frame); } catch (_e) { /* noop */ } }
    const pad = MeshBuilder.CreateDisc('siExitPad', { radius: 1.7, tessellation: 28 }, this.scene);
    pad.rotation.x = Math.PI / 2; pad.position.set(this._exitPos.x, FLOOR_Y + 0.08, this._exitPos.z); pad.parent = root;
    pad.material = this._emissive('siExitPadMat', new Color3(0.2, 0.9, 0.45), 0.55);
    pad.isPickable = false;

    // ---- Ceiling lighting (bright even interior) ----
    const lampZs = [-HALF_D * 0.5, 0, HALF_D * 0.5];
    for (let i = 0; i < lampZs.length; i++) {
      const panel = MeshBuilder.CreateBox(`siLampPanel_${i}`, { width: HALF_W * 1.2, height: 0.1, depth: 1.2 }, this.scene);
      panel.position.set(SX, FLOOR_Y + WALL_H - 0.25, SZ + lampZs[i]); panel.parent = root;
      panel.material = this._emissive(`siLampMat_${i}`, new Color3(0.9, 0.95, 1.0));
      panel.isPickable = false;
      const lamp = new PointLight(`siLamp_${i}`, new Vector3(SX, FLOOR_Y + WALL_H - 0.5, SZ + lampZs[i]), this.scene);
      lamp.diffuse = new Color3(0.95, 0.97, 1.0); lamp.intensity = 0.75; lamp.range = 34; lamp.parent = root;
    }

    this._name = shipDef?.name || 'Ship';
    return root;
  }

  showInterior(visible) {
    if (this._root) this._root.setEnabled(!!visible);
  }

  clearInterior() {
    if (this._root) {
      try { this._root.dispose(false, true); } catch (_e) { /* noop */ }
      this._root = null;
    }
    this._core = null;
  }

  tick(dt) {
    this._t += dt;
    if (this._core) {
      const p = 0.7 + Math.sin(this._t * 2.2) * 0.2;
      this._core.material.emissiveColor.set(0.2 * p + 0.05, 0.7 * p, 0.95 * p);
      this._core.rotation.y += dt * 0.6;
    }
  }

  dispose() { this.clearInterior(); }
}
